import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { subscriptionPlans } from '../data/subscriptionPlans';
import { Check } from 'lucide-react';

const SignupModal = ({ open, onClose, onLoginClick }) => {
  const { toast } = useToast();
  const { login } = useAuth();
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    password: '',
    userType: 'homeowner',
    subscriptionPlan: 'free'
  });
  const [loading, setLoading] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If professional and step 1, go to pricing selection
    if (formData.userType === 'professional' && step === 1) {
      setStep(2);
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAPI.register(formData);
      login(response.user);
      toast({
        title: 'Account Created!',
        description: `Welcome to Profind.ca, ${response.user.name}!${formData.userType === 'professional' ? ` You selected the ${formData.subscriptionPlan} plan.` : ''}`,
      });
      onClose();
      setFormData({
        name: '',
        email: '',
        password: '',
        userType: 'homeowner',
        subscriptionPlan: 'free'
      });
      setStep(1);
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'Could not create account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={() => {
      onClose();
      setStep(1);
    }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {step === 1 ? 'Join Profind.ca' : 'Choose Your Plan'}
          </DialogTitle>
        </DialogHeader>
        
        {step === 1 ? (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>I am a...</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="userType"
                    value="homeowner"
                    checked={formData.userType === 'homeowner'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm">Homeowner</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="userType"
                    value="professional"
                    checked={formData.userType === 'professional'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm">Service Professional</span>
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white">
              {formData.userType === 'professional' ? 'Continue to Pricing' : 'Create Account'}
            </Button>
            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onLoginClick}
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Sign in
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {subscriptionPlans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => setFormData({ ...formData, subscriptionPlan: plan.id })}
                  className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                    formData.subscriptionPlan === plan.id
                      ? 'border-orange-600 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  } ${plan.popular ? 'relative' : ''}`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-orange-600 text-white text-xs">
                      Popular
                    </Badge>
                  )}
                  <div className="text-center mb-3">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      ${plan.price}
                      <span className="text-sm text-gray-600 font-normal">/{plan.billingPeriod}</span>
                    </div>
                    {plan.monthlyEquivalent && (
                      <p className="text-xs text-gray-500">${plan.monthlyEquivalent}/mo</p>
                    )}
                  </div>
                  
                  <div className="bg-gray-100 rounded p-2 mb-3 text-center">
                    <p className="text-sm font-semibold text-gray-900">Lead Cost</p>
                    <p className="text-xl font-bold text-orange-600">${plan.leadCost}</p>
                    <p className="text-xs text-gray-600">per lead (avg)</p>
                  </div>

                  <div className="space-y-2">
                    {plan.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="flex items-start text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {formData.subscriptionPlan === plan.id && (
                    <div className="mt-3 text-center">
                      <Badge className="bg-orange-600 text-white">Selected</Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You can change your plan anytime. Free plan gets you started with 5 quotes/month. Upgrade when you're ready!
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;